/**
 * Cleanest Ever — Core JavaScript
 * Handles: Cart AJAX, Nav scroll, Scroll reveal, Cart drawer, Mobile nav
 */

const CE = {

  // ─── INIT ───────────────────────────────────────────
  init() {
    this.nav.init();
    this.reveal.init();
    this.cart.init();
    this.productPage.init();
    this.productCard.initHoverVideos();
    console.log('[CE] Cleanest Ever theme initialized ✓');
  },

  // ─── NAV ────────────────────────────────────────────
  nav: {
    el: null,
    init() {
      this.el = document.getElementById('mainNav');
      if (!this.el) return;
      window.addEventListener('scroll', () => this.onScroll(), { passive: true });
      this.onScroll();
    },
    onScroll() {
      const isIndex = document.body.classList.contains('template-index');
      // Always keep scrolled in theme editor — section re-renders reset scrollY to 0
      const inEditor = typeof Shopify !== 'undefined' && Shopify.designMode;
      if (isIndex && !inEditor && window.scrollY <= 80) {
        // Homepage at top on live site: transparent nav over hero
        this.el.classList.remove('scrolled', 'light');
        this.el.classList.add('dark');
      } else {
        // All other pages, theme editor, or homepage when scrolled: dark nav
        this.el.classList.add('scrolled', 'dark');
        this.el.classList.remove('light');
      }
    },
    toggleMobile() {
      const drawer = document.getElementById('mobileNavDrawer');
      const overlay = document.getElementById('mobileNavOverlay');
      const btn = document.getElementById('navHamburger');
      if (!drawer) return;
      const isOpen = drawer.style.transform === 'translateX(0%)';
      drawer.style.transform = isOpen ? 'translateX(100%)' : 'translateX(0%)';
      overlay.style.opacity = isOpen ? '0' : '1';
      overlay.style.pointerEvents = isOpen ? 'none' : 'all';
      drawer.setAttribute('aria-hidden', isOpen ? 'true' : 'false');
      btn?.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      document.body.style.overflow = isOpen ? '' : 'hidden';
    }
  },

  // ─── SCROLL REVEAL ──────────────────────────────────
  reveal: {
    init() {
      const els = document.querySelectorAll('[data-reveal]');
      if (!els.length) return;

      // Immediately reveal anything already visible in the viewport (e.g. hero on load)
      els.forEach(el => {
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) {
          el.classList.add('revealed');
        }
      });

      if (!window.IntersectionObserver) return;
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            e.target.classList.add('revealed');
            observer.unobserve(e.target);
          }
        });
      }, { threshold: 0.08, rootMargin: '0px 0px -20px 0px' });

      // Only observe elements not yet revealed
      els.forEach(el => {
        if (!el.classList.contains('revealed')) observer.observe(el);
      });
    }
  },

  // ─── CART ────────────────────────────────────────────
  cart: {
    drawerEl: null,
    overlayEl: null,
    countEls: null,
    isOpen: false,

    init() {
      this.drawerEl = document.getElementById('cartDrawer');
      this.overlayEl = document.getElementById('cartOverlay');
      this.countEls = document.querySelectorAll('#cartCount');
      if (this.overlayEl) {
        // Clicking the overlay always closes — never toggles
        this.overlayEl.addEventListener('click', () => this.close());
      }
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    },

    // Always resolve elements from DOM — cached refs may be stale
    _drawer()  { return document.getElementById('cartDrawer'); },
    _overlay() { return document.getElementById('cartOverlay'); },

    // Open: #cartDrawer is the full-screen container; CSS handles overlay + panel
    open() {
      const d = this._drawer();
      if (d) d.classList.add('open');
      document.body.style.overflow = 'hidden';
      this.isOpen = true;
      document.getElementById('cartToggleBtn')?.setAttribute('aria-expanded', 'true');
    },

    // Close: remove 'open' from the container only
    close() {
      const d = this._drawer();
      if (d) d.classList.remove('open');
      document.body.style.overflow = '';
      this.isOpen = false;
      document.getElementById('cartToggleBtn')?.setAttribute('aria-expanded', 'false');
    },

    // Toggle: check actual DOM state
    toggle() {
      const d = this._drawer();
      if (d && d.classList.contains('open')) { this.close(); } else { this.open(); }
    },

    // Shopify AJAX cart add
    async addItem(variantId, btnEl) {
      if (!variantId) return;

      const original = btnEl?.textContent;
      if (btnEl) {
        btnEl.textContent = 'Adding...';
        btnEl.disabled = true;
      }

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });

        if (!res.ok) throw new Error('Add to cart failed');
        const data = await res.json();

        // Update cart count
        await this.updateCount();

        if (btnEl) {
          btnEl.textContent = '✓ Added!';
          btnEl.style.background = 'var(--ce-coral)';
          btnEl.style.color = '#fff';
          setTimeout(() => {
            btnEl.textContent = original;
            btnEl.style.background = '';
            btnEl.style.color = '';
            btnEl.disabled = false;
          }, 1800);
        }

        // Update drawer contents
        await this.refreshDrawer();
        this.open();

      } catch (err) {
        console.error('[CE Cart]', err);
        if (btnEl) {
          btnEl.textContent = 'Try Again';
          btnEl.disabled = false;
          setTimeout(() => { btnEl.textContent = original; }, 2000);
        }
      }
    },

    async updateCount() {
      try {
        const res = await fetch('/cart.js');
        const cart = await res.json();
        const count = cart.item_count;
        document.querySelectorAll('#cartCount').forEach(el => {
          el.textContent = count;
          el.style.display = count > 0 ? '' : 'none';
        });
        return count;
      } catch(e) {
        console.error('[CE Cart] Count update failed', e);
      }
    },

    async refreshDrawer() {
      try {
        const res = await fetch('/?sections=cart-drawer');
        if (!res.ok) return;
        const data = await res.json();
        const html = data['cart-drawer'];
        if (!html) return;
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        // Swap only the panel contents — keeps the drawer container + overlay intact
        const newPanel = doc.querySelector('.cart-panel');
        const curPanel = document.querySelector('.cart-panel');
        if (newPanel && curPanel) {
          curPanel.innerHTML = newPanel.innerHTML;
        } else {
          // fallback: replace full drawer inner html
          const newDrawer = doc.querySelector('#cartDrawer');
          if (newDrawer && this.drawerEl) {
            this.drawerEl.innerHTML = newDrawer.innerHTML;
          }
        }
      } catch(e) {
        // Fallback: just update count
      }
    },

    async changeQty(lineIndex, newQty) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: lineIndex, quantity: newQty })
        });
        await this.updateCount();
        await this.refreshDrawer();
      } catch(e) {
        console.error('[CE Cart] Qty change failed', e);
      }
    },

    // context: 'drawer' (patch panel HTML) or 'page' (full reload — cart page has no AJAX section render)
    _couponEls(context) {
      const prefix = context === 'page' ? 'cp' : 'cart';
      return {
        input: document.getElementById(prefix + 'CouponInput'),
        btn: document.getElementById(prefix + 'CouponApplyBtn'),
        error: document.getElementById(prefix + 'CouponError')
      };
    },

    async applyCoupon(rawCode, context) {
      const code = (rawCode || '').trim();
      const els = this._couponEls(context);
      if (els.error) { els.error.style.display = 'none'; els.error.textContent = ''; }
      if (!code) return;

      const originalLabel = els.btn ? els.btn.textContent : '';
      if (els.btn) { els.btn.disabled = true; els.btn.textContent = 'Applying…'; }
      if (els.input) els.input.disabled = true;

      try {
        const beforeRes = await fetch('/cart.js');
        const before = await beforeRes.json();

        const res = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: code })
        });
        const data = await res.json();

        let applicable = null;
        if (Array.isArray(data.discount_codes)) {
          const match = data.discount_codes.find(d => (d.code || '').toLowerCase() === code.toLowerCase());
          if (match) applicable = !!match.applicable;
        }
        // Fallback for storefronts where discount_codes isn't in the response shape:
        // infer success from whether the cart total actually moved.
        if (applicable === null) {
          applicable = data.total_price !== before.total_price;
        }

        if (!applicable) {
          if (els.error) {
            els.error.textContent = "That code isn't valid or doesn't apply to items in your cart.";
            els.error.style.display = 'block';
          }
          if (els.btn) { els.btn.disabled = false; els.btn.textContent = originalLabel; }
          if (els.input) els.input.disabled = false;
          return;
        }

        if (context === 'page') {
          window.location.reload();
        } else {
          await this.updateCount();
          await this.refreshDrawer();
        }
      } catch (e) {
        console.error('[CE Cart] Coupon apply failed', e);
        if (els.error) {
          els.error.textContent = "Something went wrong applying that code. Please try again.";
          els.error.style.display = 'block';
        }
        if (els.btn) { els.btn.disabled = false; els.btn.textContent = originalLabel; }
        if (els.input) els.input.disabled = false;
      }
    },

    async removeCoupon(context) {
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: '' })
        });
        if (context === 'page') {
          window.location.reload();
        } else {
          await this.updateCount();
          await this.refreshDrawer();
        }
      } catch (e) {
        console.error('[CE Cart] Coupon remove failed', e);
      }
    },

    // Drawer-only: coupon input starts collapsed behind a "Have a coupon code?" link
    // so it doesn't eat into the scrollable item list on short/mobile viewports.
    toggleCouponInput() {
      const link = document.getElementById('cartCouponLinkRow');
      const row = document.getElementById('cartCouponInputRow');
      if (!link || !row) return;
      const opening = row.style.display !== 'flex';
      row.style.display = opening ? 'flex' : 'none';
      link.style.display = opening ? 'none' : 'flex';
      if (opening) document.getElementById('cartCouponInput')?.focus();
    },

    // Drawer-only: MRP/discount breakdown collapses under the savings row for the same reason.
    toggleBreakdown() {
      const rows = document.getElementById('cartBreakdownRows');
      const chev = document.getElementById('cartSavingsChev');
      if (!rows) return;
      const showing = rows.style.display === 'block';
      rows.style.display = showing ? 'none' : 'block';
      if (chev) chev.style.transform = showing ? 'rotate(0deg)' : 'rotate(180deg)';
    }
  },

  // ─── PRODUCT CARD (home / collection grid pack-size toggle) ─────
  productCard: {
    selectVariant(el) {
      if (!el || el.disabled) return;
      const card = el.closest('.product-card');
      if (!card) return;

      card.querySelectorAll('.pc-pack-pill').forEach(p => p.classList.remove('active'));
      el.classList.add('active');

      const variantId  = el.dataset.variantId;
      const price      = el.dataset.price;
      const compareStr = el.dataset.compare;
      const priceRaw   = parseInt(el.dataset.priceRaw   || '0', 10);
      const compareRaw = parseInt(el.dataset.compareRaw || '0', 10);
      const hasCompare = compareRaw > 0 && compareRaw > priceRaw;

      const priceEl = card.querySelector('.pc-price');
      if (priceEl && price) priceEl.textContent = price;

      let compareEl = card.querySelector('.pc-compare-price');
      if (hasCompare) {
        if (!compareEl && priceEl) {
          compareEl = document.createElement('span');
          compareEl.className = 'pc-compare-price';
          priceEl.insertAdjacentElement('afterend', compareEl);
        }
        if (compareEl) { compareEl.textContent = compareStr; compareEl.style.display = ''; }
      } else if (compareEl) {
        compareEl.style.display = 'none';
      }

      const addBtn = card.querySelector('.pc-add-btn');
      if (addBtn && variantId) addBtn.setAttribute('data-variant-id', variantId);
    },

    // Hover-to-play video on product cards (homepage grid + collection page).
    // Falls back to the static image everywhere touch/hover doesn't apply.
    initHoverVideos() {
      document.querySelectorAll('.pc-card-video').forEach(video => {
        const card = video.closest('.product-card');
        if (!card) return;

        card.addEventListener('mouseenter', () => {
          try { video.currentTime = 0; } catch (e) {}
          const playPromise = video.play();
          if (playPromise && playPromise.then) {
            playPromise.then(() => video.classList.add('is-playing')).catch(() => {});
          } else {
            video.classList.add('is-playing');
          }
        });

        card.addEventListener('mouseleave', () => {
          video.pause();
          video.classList.remove('is-playing');
        });
      });
    }
  },

  // ─── PRODUCT PAGE ─────────────────────────────────
  productPage: {
    init() {
      this.initStickyCTA();
      this.initVariantSelector();
      this.initTabs();
      this.initGallery();
      this.initPerUnitText();
    },

    // Extracts a pack quantity (e.g. 6, 12, 36) from a variant title like "Pack of 6"
    extractPackQty(title) {
      if (!title) return null;
      const m = title.match(/\d+/);
      return m ? parseInt(m[0], 10) : null;
    },

    // Rebuilds the "Per X x <unit> · <suffix>" note under the price for the given variant title
    updatePerUnitText(title) {
      const el = document.getElementById('pdpPerUnitText');
      if (!el) return;
      const label  = el.getAttribute('data-unit-label')  || '';
      const suffix = el.getAttribute('data-unit-suffix') || '';
      const qty = this.extractPackQty(title);
      let text = 'Per ';
      text += (qty && qty > 1) ? (qty + ' x ' + label) : label;
      if (suffix) text += ' \u00b7 ' + suffix;
      el.textContent = text;
    },

    // Sync the per-unit note with whichever pack option is active on page load
    initPerUnitText() {
      const active = document.querySelector('.pack-option.active');
      if (active) this.updatePerUnitText(active.dataset.title);
    },

    initStickyCTA() {
      const stickyCta = document.getElementById('pdpStickyCta') || document.getElementById('stickyCta');
      const mainBtn = document.getElementById('mainAddBtn');
      if (!stickyCta || !mainBtn) return;
      const observer = new IntersectionObserver(([e]) => {
        stickyCta.classList.toggle('visible', !e.isIntersecting);
      }, { threshold: 0 });
      observer.observe(mainBtn);
    },

    initVariantSelector() {
      const self = this;
      document.querySelectorAll('.pack-option').forEach(option => {
        option.addEventListener('click', function() {
          document.querySelectorAll('.pack-option').forEach(o => o.classList.remove('active'));
          this.classList.add('active');

          // Update the per-unit note (e.g. "Per 12 x 200ml bottle · Taxes included")
          self.updatePerUnitText(this.dataset.title);

          const variantId  = this.dataset.variantId;
          const price      = this.dataset.price;       // formatted e.g. "₹349"
          const compareStr = this.dataset.compare;     // formatted e.g. "₹396"
          const priceRaw   = parseInt(this.dataset.priceRaw   || '0', 10);
          const compareRaw = parseInt(this.dataset.compareRaw || '0', 10);
          const hasCompare = compareRaw > 0 && compareRaw > priceRaw;

          // Hidden variant input
          const variantInput = document.querySelector('input[name="id"], #pdpVariantId');
          if (variantInput && variantId) variantInput.value = variantId;

          // Main price
          const priceEl = document.querySelector('.pdp-price');
          if (priceEl && price) priceEl.textContent = price;

          // Compare-at price — create element if it doesn't exist yet
          const priceBlock = document.querySelector('.pdp-price-block');
          let origEl  = document.querySelector('.pdp-price-orig');
          let saveTag = document.querySelector('.pdp-save-tag');

          if (hasCompare) {
            if (!origEl && priceBlock) {
              origEl = document.createElement('span');
              origEl.className = 'pdp-price-orig';
              priceBlock.appendChild(origEl);
            }
            if (origEl) { origEl.textContent = compareStr; origEl.style.display = ''; }

            const pct = Math.round((compareRaw - priceRaw) * 100 / compareRaw);
            if (!saveTag && priceBlock && pct > 0) {
              saveTag = document.createElement('span');
              saveTag.className = 'pdp-save-tag';
              priceBlock.appendChild(saveTag);
            }
            if (saveTag) { saveTag.textContent = 'Save ' + pct + '%'; saveTag.style.display = ''; }
          } else {
            if (origEl)  origEl.style.display  = 'none';
            if (saveTag) saveTag.style.display = 'none';
          }

          // ATC button
          const btn = document.getElementById('mainAddBtn');
          if (btn && price) btn.textContent = 'Add to Cart — ' + price;

          // Sticky bar
          const stickyPrice = document.querySelector('.sticky-price');
          if (stickyPrice && price) stickyPrice.textContent = price;
          const stickyBtn = document.getElementById('stickyAddBtn');
          if (stickyBtn && price) stickyBtn.textContent = 'Add to Cart — ' + price;
        });
      });
    },

    initTabs() {
      // Bind click listeners to all tab buttons via data-tab attribute
      document.querySelectorAll('.pdp-tab-btn[data-tab]').forEach(btn => {
        btn.addEventListener('click', function() {
          const name = this.dataset.tab;
          document.querySelectorAll('.pdp-tab-content').forEach(t => t.classList.remove('active'));
          document.querySelectorAll('.pdp-tab-btn').forEach(b => b.classList.remove('active'));
          const target = document.getElementById('tab-' + name);
          if (target) target.classList.add('active');
          this.classList.add('active');
        });
      });

      // Also handle hash-based deep-linking
      const hash = window.location.hash.replace('#tab-', '');
      if (hash && document.getElementById('tab-' + hash)) {
        document.querySelectorAll('.pdp-tab-content').forEach(t => t.classList.remove('active'));
        document.querySelectorAll('.pdp-tab-btn').forEach(b => b.classList.remove('active'));
        document.getElementById('tab-' + hash).classList.add('active');
        const hashBtn = document.querySelector('[data-tab="' + hash + '"]');
        if (hashBtn) hashBtn.classList.add('active');
      }
    },

    initGallery() {
      // Use data-img-src attribute set on each thumb in the Liquid template
      document.querySelectorAll('.pdp-thumb[data-img-src]').forEach(thumb => {
        thumb.addEventListener('click', function() {
          const src = this.getAttribute('data-img-src');
          // Swap main image
          const mainImg = document.getElementById('pdpMainImgEl');
          if (mainImg && src) {
            mainImg.src = src;
            mainImg.srcset = '';   // clear srcset so browser uses our src
          }
          // Update active state
          document.querySelectorAll('.pdp-thumb').forEach(t => t.classList.remove('active'));
          this.classList.add('active');
        });
      });
    }
  }
};

// Expose CE on window so inline onclick attributes (header, cart drawer, etc.) can reach it.
// `const CE` is script-scoped and NOT a window property — without this line,
// onclick="CE.cart.toggle()" throws ReferenceError.
window.CE = CE;

// Boot on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => CE.init());
} else {
  CE.init();
}

// Re-init nav when any section re-renders in the theme editor
document.addEventListener('shopify:section:load', () => {
  CE.nav.el = document.getElementById('mainNav');
  if (CE.nav.el) CE.nav.onScroll();
});

// ─── GLOBAL HELPERS ─────────────────────────────────

// Cart open/close — window-level, only touch #cartDrawer container
window.closeCart = function() {
  var d = document.getElementById('cartDrawer');
  if (d) d.classList.remove('open');
  document.body.style.overflow = '';
};
window.openCart = function() {
  var d = document.getElementById('cartDrawer');
  if (d) d.classList.add('open');
  document.body.style.overflow = 'hidden';
};

// Event delegation fallback — catches data-cart-close / data-cart-open clicks
// ev